import { forwardRef, MouseEventHandler } from 'react';
import { twJoin } from 'tailwind-merge';

import { TamagoshiIconComponent } from '@/core/tamagoshiTailwind/interfaces';

type IconButtonProps = {
  /** Label used for the tooltip and accessibility label */
  'aria-label': string;
  /** Whether the button should be disabled */
  disabled?: boolean;
  /** Icon component to be displayed in the button */
  icon: TamagoshiIconComponent;
  /** The click handler */
  onClick: MouseEventHandler<HTMLButtonElement>;
  /** The size of the button */
  size?: 'S' | 'M';
};

/**
 * A textless icon button.
 *
 * See the related [Figma][figma] for more details.
 *
 * [figma]: https://www.figma.com/design/KAiRlBccr6bFYmKby9wQrR?node-id=15902-17958
 */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ icon: Icon, size = 'S', ...props }: IconButtonProps, ref) => (
    <button
      className={twJoin(
        `flex appearance-none items-center rounded-card bg-foundation-inverted
        text-foundation-primary hover:bg-foundation-overlayHover
        disabled:bg-foundation-inverted disabled:text-foundation-disabled`,
        size === 'M' && 'size-[30px] rounded-full p-xs',
        size === 'S' && 'size-[24px]',
      )}
      ref={ref}
      title={props['aria-label']}
      type="button"
      {...props}
    >
      <Icon
        className={twJoin(
          size === 'M' && 'size-[22px]',
          size === 'S' && 'size-[24px]',
        )}
      />
    </button>
  ),
);

IconButton.displayName = 'IconButton';
